import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getAppTree, getPage, AppTree, Page, PageTreeNode } from '../services/api';
import DocSidebar from '../components/DocSidebar';
import Breadcrumb from '../components/Breadcrumb';
import Header from '../components/Header';

const slugify = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');

const headingComponent = (level: 1 | 2 | 3) =>
  function Heading({ children }: { children?: React.ReactNode }) {
    const text = String(children);
    const Tag = `h${level}` as const;
    return <Tag id={slugify(text)}>{children}</Tag>;
  };

// Depth-first search for the first page (a leaf with content) in the tree
const findFirstPage = (nodes: PageTreeNode[], parentPath: string[] = []): string[] | null => {
  for (const node of nodes) {
    const path = [...parentPath, node.slug];
    if (node.hasContent) return path;
    const found = findFirstPage(node.children, path);
    if (found) return found;
  }
  return null;
};

const MenuIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
    <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
    <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
  </svg>
);

// "https://github.com/owner/repo" -> "owner/repo"
const repoLabel = (url: string) => {
  try {
    return new URL(url).pathname.replace(/^\/|\/$/g, '');
  } catch {
    return url;
  }
};

const GithubIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 shrink-0">
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
  </svg>
);

const Skeleton = () => (
  <div className="min-h-screen bg-neutral-50">
    <Header />

    <div className="sticky top-12 z-10 border-b border-neutral-700 bg-neutral-800">
      <div className="flex items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="h-8 w-8 animate-pulse rounded-md border border-neutral-600 lg:hidden" />
        <div className="h-4 w-40 animate-pulse rounded bg-neutral-700" />
      </div>
    </div>

    <div className="grid animate-pulse grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[220px_1fr_180px] lg:px-8">
      <div className="hidden space-y-2 lg:block">
        <div className="mb-3 h-4 w-2/3 rounded bg-neutral-200" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-6 rounded bg-neutral-200" />
        ))}
      </div>
      <ArticleSkeleton />
      <div className="hidden space-y-2 lg:block">
        <div className="mb-2 h-3 w-24 rounded bg-neutral-200" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-4 rounded bg-neutral-200" />
        ))}
      </div>
    </div>
  </div>
);

const ArticleSkeleton = () => (
  <article className="max-w-4xl animate-pulse space-y-3 rounded-xl border border-neutral-200 bg-white p-8">
    <div className="h-7 w-2/3 rounded bg-neutral-200" />
    <div className="h-4 w-full rounded bg-neutral-200" />
    <div className="h-4 w-full rounded bg-neutral-200" />
    <div className="h-4 w-5/6 rounded bg-neutral-200" />
    <div className="h-4 w-full rounded bg-neutral-200" />
    <div className="h-4 w-3/4 rounded bg-neutral-200" />
    <div className="!mt-8 h-5 w-1/3 rounded bg-neutral-200" />
    <div className="h-4 w-full rounded bg-neutral-200" />
    <div className="h-4 w-full rounded bg-neutral-200" />
    <div className="h-4 w-4/6 rounded bg-neutral-200" />
    <div className="!mt-8 h-5 w-1/4 rounded bg-neutral-200" />
    <div className="h-4 w-full rounded bg-neutral-200" />
    <div className="h-4 w-2/3 rounded bg-neutral-200" />
  </article>
);

const StatusScreen = ({ message }: { message: string }) => (
  <div className="min-h-screen bg-neutral-50">
    <Header />
    <div className="flex min-h-[calc(100vh-3rem)] items-center justify-center px-4 text-center">
      <div>
        <p className="text-neutral-500">{message}</p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium text-brand-600 hover:text-brand-700">
          &larr; All apps
        </Link>
      </div>
    </div>
  </div>
);

const AppDocLayout = () => {
  const { slug } = useParams<{ slug: string }>();
  const splat = useParams()['*'] ?? '';
  const pagePath = useMemo(() => splat.split('/').filter(Boolean), [splat]);

  const [tree, setTree] = useState<AppTree | null>(null);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [page, setPage] = useState<Page | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setTree(null);
    setTreeError(null);
    getAppTree(slug)
      .then(setTree)
      .catch(() => setTreeError('This app could not be found.'));
  }, [slug]);

  useEffect(() => {
    if (!slug || pagePath.length === 0) return;
    setIsLoadingPage(true);
    setPageError(null);
    setPage(null);
    getPage(slug, pagePath)
      .then(setPage)
      .catch(() => setPageError('This page could not be found.'))
      .finally(() => setIsLoadingPage(false));
  }, [slug, pagePath]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [splat]);

  const toc = useMemo(() => {
    if (!page) return [];
    const matches = [...page.content.matchAll(/^(#{1,3})\s+(.+)$/gm)];
    return matches.map((m) => ({ level: m[1].length, text: m[2], slug: slugify(m[2]) }));
  }, [page]);

  if (!slug || treeError) {
    return <StatusScreen message={treeError ?? 'This app could not be found.'} />;
  }

  if (!tree) {
    return <Skeleton />;
  }

  if (pagePath.length === 0) {
    const firstPath = findFirstPage(tree.tree);
    if (!firstPath) {
      return <StatusScreen message="No pages have been published for this app yet." />;
    }
    return <Navigate to={`/${slug}/${firstPath.join('/')}`} replace />;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />

      <div className="sticky top-12 z-10 border-b border-neutral-700 bg-neutral-800">
        <div className="flex items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="rounded-md border border-neutral-600 p-1.5 text-neutral-300 hover:text-white lg:hidden"
            aria-label="Open navigation"
          >
            <MenuIcon />
          </button>
          {page ? (
            <Breadcrumb appSlug={slug} appName={tree.name} items={page.breadcrumb} />
          ) : (
            <div className="h-4 w-40 animate-pulse rounded bg-neutral-700" />
          )}
        </div>
      </div>

      <div
        className={`fixed inset-0 z-30 lg:hidden ${mobileNavOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!mobileNavOpen}
      >
        <div
          className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
            mobileNavOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileNavOpen(false)}
        />
        <div
          className={`absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col overflow-y-auto bg-white shadow-soft transition-transform duration-300 ease-out ${
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between border-b border-neutral-100 p-4">
            <p className="truncate font-medium text-neutral-900">{tree.name}</p>
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              className="rounded-md p-1 text-neutral-400 hover:text-neutral-800"
              aria-label="Close navigation"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="flex-1 p-4">
            {tree.repo_url && (
              <a
                href={tree.repo_url}
                target="_blank"
                rel="noopener noreferrer"
                title={tree.repo_url}
                className="mb-4 flex items-center gap-1.5 truncate text-xs text-neutral-400 no-underline hover:text-neutral-700"
              >
                <GithubIcon />
                <span className="truncate">{repoLabel(tree.repo_url)}</span>
              </a>
            )}
            <DocSidebar appSlug={slug} tree={tree.tree} activePath={pagePath} />
          </div>
        </div>
      </div>

      <main className="grid grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[220px_1fr_180px] lg:px-8">
        <aside className="hidden lg:block">
          <div className="sticky top-32 max-h-[calc(100vh-9rem)] overflow-y-auto pb-8">
            <p className="mb-3 truncate px-2.5 text-sm font-semibold text-neutral-900">{tree.name}</p>
            {tree.repo_url && (
              <a
                href={tree.repo_url}
                target="_blank"
                rel="noopener noreferrer"
                title={tree.repo_url}
                className="mb-4 flex items-center gap-1.5 truncate px-2.5 text-xs text-neutral-400 no-underline hover:text-neutral-700"
              >
                <GithubIcon />
                <span className="truncate">{repoLabel(tree.repo_url)}</span>
              </a>
            )}
            <DocSidebar appSlug={slug} tree={tree.tree} activePath={pagePath} />
          </div>
        </aside>

        {isLoadingPage && !page && <ArticleSkeleton />}

        {pageError && !isLoadingPage && (
          <article className="max-w-4xl rounded-xl border border-neutral-200 bg-white p-8 text-neutral-500">
            {pageError}
          </article>
        )}

        {page && !pageError && (
          <article className="max-w-4xl animate-fade-in rounded-xl border border-neutral-200 bg-white p-8 shadow-soft">
            <div className="prose prose-neutral max-w-none prose-a:text-brand-600 prose-headings:scroll-mt-24">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: headingComponent(1),
                  h2: headingComponent(2),
                  h3: headingComponent(3),
                }}
              >
                {page.content}
              </ReactMarkdown>
            </div>

            {(page.prev || page.next) && (
              <div className="mt-10 flex items-center justify-between gap-4 border-t border-neutral-200 pt-6 text-sm">
                {page.prev ? (
                  <Link
                    to={`/${slug}/${pagePath.slice(0, -1).concat(page.prev.slug).join('/')}`}
                    className="rounded-lg border border-neutral-200 px-4 py-2 no-underline text-neutral-600 hover:border-neutral-300 hover:text-neutral-900"
                  >
                    &larr; {page.prev.title}
                  </Link>
                ) : (
                  <span />
                )}
                {page.next && (
                  <Link
                    to={`/${slug}/${pagePath.slice(0, -1).concat(page.next.slug).join('/')}`}
                    className="rounded-lg border border-neutral-200 px-4 py-2 no-underline text-neutral-600 hover:border-neutral-300 hover:text-neutral-900"
                  >
                    {page.next.title} &rarr;
                  </Link>
                )}
              </div>
            )}
          </article>
        )}

        {page && !pageError && toc.length > 0 && (
          <aside className="hidden lg:block">
            <div className="sticky top-32 text-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">On this page</p>
              <ul className="space-y-1.5">
                {toc.map((item) => (
                  <li key={item.slug} style={{ paddingLeft: (item.level - 1) * 12 }}>
                    <a href={`#${item.slug}`} className="text-neutral-500 no-underline hover:text-neutral-900">
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </main>
    </div>
  );
};

export default AppDocLayout;
